// Keyword Research & Strategy
// This file contains keyword mapping for all pages to improve SEO targeting

export interface KeywordGroup {
  primary: string; // Main keyword for the page
  secondary: string[]; // Supporting keywords
  longTail: string[]; // Long-tail keywords for better targeting
  userIntent: 'informational' | 'commercial' | 'transactional' | 'navigational';
}

export interface PageKeywords {
  [pagePath: string]: KeywordGroup;
}

// Keyword mapping for different pages
export const pageKeywords: PageKeywords = {
  // Homepage
  '/': {
    primary: 'software development company',
    secondary: [
      'custom software development',
      'embedded software development',
      'graphics programming',
      'gpgpu development',
      'realtime embedded systems',
      'algorithm design',
      'graphic programming products',
      'software development products',
      'gpu accelerated solutions',
      'video software development',
      'cross-platform development',
      'professional software development',
    ],
    longTail: [
      'professional software development company',
      'custom embedded software development products',
      'gpu accelerated graphics programming',
      'realtime embedded system solutions',
      'advanced algorithm design products',
      'cross-platform software development',
      'graphics programming and algorithm design',
      'professional software development with 25 years experience',
      'gpgpu and graphics development products',
      'custom software solutions for video editing',
      'embedded software development company',
      'graphics programming specialists',
    ],
    userIntent: 'commercial',
  },

  // Products/Video Plugin Pages
  '/video-plugin-framework': {
    primary: 'video plugin',
    secondary: [
      'adobe premiere pro plugin',
      'davinci resolve plugin',
      'final cut pro plugin',
      'sony vegas pro plugin',
      'nuke plugin',
      'natron plugin',
      'ofx plugin',
      'openfx plugin',
      'vfx',
      'video editing plugin',
      'gpu video plugin',
      'professional video plugin',
    ],
    longTail: [
      'best video plugins for adobe premiere',
      'davinci resolve video editing plugins',
      'gpu accelerated video plugins',
      'professional video plugin solutions',
      'ofx plugins for video editing',
      'openfx video plugins',
      'vfx plugins for premiere pro',
      'final cut pro video plugins',
      'sony vegas pro video editing plugins',
      'nuke compositing plugins',
      'natron vfx plugins',
    ],
    userIntent: 'commercial',
  },

  // Blog/News Pages
  '/news': {
    primary: 'video plugin news',
    secondary: [
      'video editing blog',
      'software development news',
      'video technology updates',
      'epen blog',
      'video plugin industry news',
    ],
    longTail: [
      'latest video plugin development news',
      'video editing software updates',
      'video technology trends and insights',
      'professional video plugin blog',
    ],
    userIntent: 'informational',
  },

  // FAQ Page
  '/faq': {
    primary: 'video plugin faq',
    secondary: [
      'epen faq',
      'video plugin questions',
      'plugin support',
      'video software help',
      'frequently asked questions',
    ],
    longTail: [
      'frequently asked questions about video plugins',
      'epen video plugin support',
      'video plugin installation help',
      'video editing software questions',
    ],
    userIntent: 'informational',
  },

  // Contact Page
  '/contact-us': {
    primary: 'contact video plugin developer',
    secondary: [
      'contact epen',
      'video plugin support contact',
      'custom plugin inquiry',
      'video product setup contact',
    ],
    longTail: [
      'contact professional video plugin developer',
      'get quote for custom video plugin',
      'contact video software development company',
    ],
    userIntent: 'transactional',
  },

  // About/Company Pages
  '/our-clients': {
    primary: 'video plugin development clients',
    secondary: [
      'epen clients',
      'video plugin case studies',
      'video software portfolio',
      'client success stories',
    ],
    longTail: [
      'video plugin development client portfolio',
      'successful video plugin projects',
      'video software development case studies',
    ],
    userIntent: 'commercial',
  },
};

/**
 * Get keywords for a specific page path
 * @param path - The page path (e.g., '/', '/news', '/video-plugin-framework/product-name')
 * @returns KeywordGroup for the page or default keywords
 */
export function getKeywordsForPage(path: string): KeywordGroup {
  // Check exact match first
  if (pageKeywords[path]) {
    return pageKeywords[path];
  }

  // Check for dynamic routes (e.g., /video-plugin-framework/[url], /news/[slug])
  if (path.startsWith('/video-plugin-framework/')) {
    return pageKeywords['/video-plugin-framework'];
  }

  if (path.startsWith('/news/')) {
    return pageKeywords['/news'];
  }

  if (path.startsWith('/our-clients/')) {
    return pageKeywords['/our-clients'];
  }

  // Default keywords
  return {
    primary: 'epen video plugin',
    secondary: ['video editing', 'adobe premiere', 'davinci resolve', 'plugin development'],
    longTail: ['professional video plugin development'],
    userIntent: 'commercial',
  };
}

/**
 * Format keywords as a comma-separated string for meta tags
 * @param keywords - KeywordGroup object
 * @param includeLongTail - Whether to include long-tail keywords (default: false for meta tags)
 * @returns Comma-separated keywords string
 */
export function formatKeywords(keywords: KeywordGroup, includeLongTail: boolean = false): string {
  const allKeywords = [keywords.primary, ...keywords.secondary];
  if (includeLongTail) {
    allKeywords.push(...keywords.longTail);
  }
  return allKeywords.join(', ');
}

/**
 * Get keywords for a product page based on product data
 * @param productName - Name of the product
 * @param productCategory - Category of the product (optional)
 * @returns KeywordGroup customized for the product
 */
export function getProductKeywords(productName: string, productCategory?: string): KeywordGroup {
  const baseKeywords = pageKeywords['/video-plugin-framework'];

  // Core plugin platform keywords
  const platformKeywords = [
    'DaVinci Resolve Plugin',
    'Natron Plugin',
    'Nuke Plugin',
    'Sony Vegas Pro Plugin',
    'Final Cut Pro Plugin',
    'Adobe Premiere Pro Plugin',
    'OFX Plugin',
    'OpenFX Plugin',
    'VFX',
  ];

  return {
    primary: `${productName} plugin`,
    secondary: [
      ...baseKeywords.secondary,
      ...platformKeywords,
      productName.toLowerCase(),
      `${productName} for premiere`,
      `${productName} for davinci resolve`,
      `${productName} for final cut pro`,
      `${productName} for sony vegas`,
      `${productName} ofx plugin`,
      `${productName} openfx plugin`,
    ],
    longTail: [
      `buy ${productName} plugin`,
      `${productName} video plugin review`,
      `how to use ${productName} plugin`,
      `${productName} plugin features`,
      `${productName} for adobe premiere pro`,
      `${productName} for davinci resolve`,
      `${productName} vfx plugin`,
      `best ${productName} plugin`,
      ...baseKeywords.longTail,
    ],
    userIntent: 'transactional',
  };
}

/**
 * Get keywords for a blog post/article
 * @param postTitle - Title of the blog post
 * @param postTags - Tags associated with the post (optional)
 * @returns KeywordGroup customized for the article
 */
export function getArticleKeywords(postTitle: string, postTags?: string[]): KeywordGroup {
  const baseKeywords = pageKeywords['/news'];

  // Extract potential keywords from title
  const titleKeywords = postTitle
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 3)
    .slice(0, 5);

  return {
    primary: postTitle.toLowerCase(),
    secondary: [...baseKeywords.secondary, ...titleKeywords, ...(postTags || [])],
    longTail: [
      `${postTitle} guide`,
      `learn about ${postTitle}`,
      `${postTitle} tutorial`,
      ...baseKeywords.longTail,
    ],
    userIntent: 'informational',
  };
}
